import React, { useEffect, useState } from "react";
import api, { API_BASE } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { eur, slotLabel, spaceTypeLabel, formatApiError } from "../lib/utils.helpers";
import { toast } from "sonner";
import { Download, Trash2, Users, Calendar, Ban, BarChart3, Coins } from "lucide-react";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [spaces, setSpaces] = useState([]);

  const load = async () => {
    const [s, u, r, b, sp] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/users"),
      api.get("/admin/reservations"), api.get("/admin/blocked-slots"),
      api.get("/spaces"),
    ]);
    setStats(s.data); setUsers(u.data); setReservations(r.data); setBlocked(b.data); setSpaces(sp.data);
  };
  useEffect(() => { load().catch(() => toast.error("Erreur de chargement")); }, []);

  return (
    <div className="container-x py-12" data-testid="admin-page">
      <p className="overline text-[#2B8B8E]">Administration</p>
      <h1 className="text-4xl sm:text-5xl font-light mt-2 mb-10">Tableau de bord</h1>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard icon={Users} label="Utilisateurs" value={stats.total_users} testid="stat-users" />
          <StatCard icon={Calendar} label="Réservations" value={stats.total_reservations} testid="stat-reservations" />
          <StatCard icon={BarChart3} label="Aujourd'hui" value={stats.today_reservations} testid="stat-today" />
          <StatCard icon={Coins} label="Chiffre d'affaires" value={eur(stats.revenue)} testid="stat-revenue" />
        </div>
      )}

      <Tabs defaultValue="reservations">
        <TabsList className="mb-6 flex-wrap">
          <TabsTrigger value="reservations" data-testid="tab-reservations">Réservations</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Utilisateurs</TabsTrigger>
          <TabsTrigger value="spaces" data-testid="tab-spaces">Espaces & tarifs</TabsTrigger>
          <TabsTrigger value="blocked" data-testid="tab-blocked">Créneaux bloqués</TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-stats">Statistiques</TabsTrigger>
        </TabsList>

        <TabsContent value="reservations">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold">Toutes les réservations</h3>
            <Button asChild variant="outline" data-testid="export-csv-btn">
              <a href={`${API_BASE}/admin/export/reservations.csv`} target="_blank" rel="noreferrer">
                <Download size={14} className="mr-2" /> Exporter CSV
              </a>
            </Button>
          </div>
          <div className="border border-border rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr><Th>Date</Th><Th>Client</Th><Th>Espace</Th><Th>Créneau</Th><Th>Prix</Th><Th>Statut</Th><Th>Paiement</Th><Th></Th></tr>
              </thead>
              <tbody>
                {reservations.map((r) => (
                  <tr key={r.id} className="border-t border-border" data-testid={`admin-res-${r.id}`}>
                    <Td>{r.date}</Td>
                    <Td><div className="font-medium">{r.user_name}</div><div className="text-xs text-muted-foreground">{r.user_email}</div></Td>
                    <Td>{r.space_name}</Td>
                    <Td>{r.slot_type === "hour" ? `${r.start_hour}h-${r.end_hour}h` : slotLabel(r.slot_type)}</Td>
                    <Td>{eur(r.total_price)}</Td>
                    <Td><Badge color={r.status === "confirmed" ? "green" : "red"}>{r.status}</Badge></Td>
                    <Td><Badge color={r.payment_status === "paid" ? "green" : "amber"}>{r.payment_status}</Badge></Td>
                    <Td>
                      {r.status !== "cancelled" && (
                        <Button size="sm" variant="ghost" onClick={async () => { await api.patch(`/admin/reservations/${r.id}`, { status: "cancelled" }); load(); }} data-testid={`admin-cancel-${r.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="users">
          <h3 className="text-xl font-semibold mb-4">Comptes utilisateurs</h3>
          <div className="border border-border rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50"><tr><Th>Nom</Th><Th>Email</Th><Th>Rôle</Th><Th>Créé le</Th><Th></Th></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-border" data-testid={`admin-user-${u.id}`}>
                    <Td>{u.name}</Td>
                    <Td>{u.email}</Td>
                    <Td><Badge color={u.role === "admin" ? "terracotta" : "gray"}>{u.role}</Badge></Td>
                    <Td>{new Date(u.created_at).toLocaleDateString("fr-FR")}</Td>
                    <Td>
                      {u.role !== "admin" && (
                        <Button size="sm" variant="ghost" onClick={async () => { await api.delete(`/admin/users/${u.id}`); load(); toast.success("Utilisateur supprimé"); }} data-testid={`admin-del-user-${u.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="spaces">
          <h3 className="text-xl font-semibold mb-4">Espaces & gestion des tarifs</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {spaces.map((s) => <SpaceEditor key={s.id} space={s} onSaved={load} />)}
          </div>
        </TabsContent>

        <TabsContent value="blocked">
          <h3 className="text-xl font-semibold mb-4">Bloquer un créneau</h3>
          <BlockedForm spaces={spaces} onCreated={load} />
          <div className="mt-6 border border-border rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50"><tr><Th>Date</Th><Th>Espace</Th><Th>Créneau</Th><Th>Raison</Th><Th></Th></tr></thead>
              <tbody>
                {blocked.map((b) => {
                  const sp = spaces.find((s) => s.id === b.space_id);
                  return (
                    <tr key={b.id} className="border-t border-border">
                      <Td>{b.date}</Td>
                      <Td>{sp?.name || b.space_id}</Td>
                      <Td>{b.slot_type === "hour" ? `${b.start_hour}h-${b.end_hour}h` : slotLabel(b.slot_type)}</Td>
                      <Td>{b.reason}</Td>
                      <Td>
                        <Button size="sm" variant="ghost" onClick={async () => { await api.delete(`/admin/blocked-slots/${b.id}`); load(); }} data-testid={`del-blocked-${b.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="stats">
          <h3 className="text-xl font-semibold mb-4">Occupation par type d'espace</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {stats?.by_type?.map((b) => (
              <div key={b.type} className="border border-border rounded-2xl p-6 bg-card">
                <p className="overline text-[#0D2C4D]">{spaceTypeLabel(b.type)}</p>
                <p className="text-3xl font-light mt-2">{b.count}</p>
                <p className="text-xs text-muted-foreground">réservations · {eur(b.revenue)}</p>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Th = ({ children }) => <th className="text-left text-xs font-semibold uppercase tracking-wider px-4 py-3">{children}</th>;
const Td = ({ children }) => <td className="px-4 py-3 align-top">{children}</td>;

function Badge({ color = "gray", children }) {
  const m = { green: "bg-[#0D2C4D]/20 text-[#5d7d6a]", amber: "bg-[#E0B66A]/30 text-[#8b6b2d]", red: "bg-destructive/20 text-destructive", terracotta: "bg-[#2B8B8E]/20 text-[#2B8B8E]", gray: "bg-muted text-muted-foreground" };
  return <span className={`inline-block px-2 py-0.5 text-xs rounded-full ${m[color]}`}>{children}</span>;
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="border border-border rounded-2xl p-6 bg-card" data-testid={testid}>
      <Icon size={20} className="text-[#2B8B8E] mb-3" />
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-3xl font-light mt-1">{value}</p>
    </div>
  );
}

function BlockedForm({ spaces, onCreated }) {
  const [form, setForm] = useState({ space_id: "", date: "", slot_type: "full_day", start_hour: "", end_hour: "", reason: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, start_hour: form.slot_type === "hour" ? parseInt(form.start_hour) : null, end_hour: form.slot_type === "hour" ? parseInt(form.end_hour) : null };
      await api.post("/admin/blocked-slots", payload);
      toast.success("Créneau bloqué");
      setForm({ space_id: "", date: "", slot_type: "full_day", start_hour: "", end_hour: "", reason: "" });
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <form onSubmit={submit} className="border border-border rounded-2xl p-5 bg-card grid grid-cols-1 md:grid-cols-6 gap-3 items-end" data-testid="blocked-form">
      <div className="md:col-span-2">
        <Label>Espace</Label>
        <Select value={form.space_id} onValueChange={(v) => setForm({ ...form, space_id: v })}>
          <SelectTrigger data-testid="blocked-space"><SelectValue placeholder="Choisir" /></SelectTrigger>
          <SelectContent>{spaces.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>Date</Label><Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="blocked-date" /></div>
      <div>
        <Label>Créneau</Label>
        <Select value={form.slot_type} onValueChange={(v) => setForm({ ...form, slot_type: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="full_day">Journée</SelectItem>
            <SelectItem value="morning">Matinée</SelectItem>
            <SelectItem value="afternoon">Après-midi</SelectItem>
            <SelectItem value="hour">Horaire</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {form.slot_type === "hour" ? (
        <>
          <div><Label>De (h)</Label><Input type="number" min="8" max="18" value={form.start_hour} onChange={(e) => setForm({ ...form, start_hour: e.target.value })} /></div>
          <div><Label>À (h)</Label><Input type="number" min="9" max="19" value={form.end_hour} onChange={(e) => setForm({ ...form, end_hour: e.target.value })} /></div>
        </>
      ) : <div className="md:col-span-2"><Label>Raison</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>}
      <Button type="submit" className="bg-[#2B8B8E] hover:bg-[#1F6F73] text-white" data-testid="blocked-submit">Bloquer</Button>
    </form>
  );
}

function SpaceEditor({ space, onSaved }) {
  const [form, setForm] = useState({
    price_hour: space.price_hour, price_half_day: space.price_half_day,
    price_full_day: space.price_full_day, capacity: space.capacity, active: space.active,
  });
  const save = async () => {
    try {
      await api.patch(`/admin/spaces/${space.id}`, form);
      toast.success("Espace mis à jour");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="border border-border rounded-2xl p-5 bg-card" data-testid={`space-editor-${space.type}`}>
      <p className="overline text-[#0D2C4D] text-[10px]">{spaceTypeLabel(space.type)}</p>
      <h4 className="font-semibold mt-1 mb-3">{space.name}</h4>
      <div className="space-y-2 mb-4">
        <div><Label className="text-xs">Capacité</Label><Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) })} /></div>
        <div><Label className="text-xs">Prix horaire (€)</Label><Input type="number" step="0.5" value={form.price_hour} onChange={(e) => setForm({ ...form, price_hour: parseFloat(e.target.value) })} /></div>
        <div><Label className="text-xs">Prix demi-journée (€)</Label><Input type="number" step="0.5" value={form.price_half_day} onChange={(e) => setForm({ ...form, price_half_day: parseFloat(e.target.value) })} /></div>
        <div><Label className="text-xs">Prix journée (€)</Label><Input type="number" step="0.5" value={form.price_full_day} onChange={(e) => setForm({ ...form, price_full_day: parseFloat(e.target.value) })} /></div>
      </div>
      <Button onClick={save} className="w-full bg-[#2B8B8E] hover:bg-[#1F6F73] text-white" size="sm" data-testid={`save-space-${space.type}`}>Enregistrer</Button>
    </div>
  );
}
